import rankingEmpty from '@assets/img/svg/ranking-empty.svg'
import IconsEnum from '@enums/icons.enum'
import { Campaign, ChartDoughnut, ChartHorizontalBarChartjs, Gamification, onLangChange, Ranking } from '@hub/shared'
import EmptyPageComponent from '@src/components/empty-page/empty-page.component'
import { ChartData } from 'chart.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './pointing.page.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  userRanking: Ranking
  gamifications: Gamification[]
  campaign: Campaign
}

export default ({ userRanking, gamifications, campaign }: Props) => {
  const [t, i18n] = useTranslation('engage')
  const [rankingToData, setRankingToData] = useState<ChartData>(null)
  const referralGamification = useMemo(() => gamifications.find((g) => g.type === 'referral' && g.enabled), [gamifications])
  const spreadsheetGamification = useMemo(() => gamifications.find((g) => g.type === 'spreadsheet' && g.enabled), [gamifications])
  const socialNetworkGamification = useMemo(
    () => gamifications.find((g) => g.type === campaign.socialNetworks[0] && g.enabled),
    [gamifications, campaign]
  )
  const [lang, setLang] = useState(i18n.language)
  const iconsEnum = useMemo(() => IconsEnum, [])
  const [weeklyRanking, setWeeklyRanking] = useState<ChartData>(null)
  const mainSocialNetworkFields = useMemo(
    () => [
      'stories',
      'comments',
      'likes',
      'participate',
      'reels',
      'likesReels',
      'commentsReels',
      'video',
      'tweet',
      'retweet',
      'views',
      'shares',
    ],
    []
  )

  const parseMetricsColor = (metric: string) => {
    switch (metric) {
      case 'stories':
        return '#a08912'
      case 'comments':
        return '#9cdce3'
      case 'commentsReels':
        return '#6cd87b'
      case 'likesReels':
        return '#be6ef7'
      case 'likes':
        return '#7C70D1'
      case 'participate':
        return '#F42A9C'
      case 'reels':
        return '#e58a19'
      case 'video':
        return '#FF0000'
      case 'tweet':
        return '#1DA1F2'
      case 'retweet':
        return '#299C39'
      case 'views':
        return '#000000'
      case 'shares':
        return '#e87d7d'
      default:
        if (metric.includes('score')) {
          return `#2af4${Math.floor(Math.random() * (15 - 0 + 1) + 0)
            .toString(16)
            .padStart(2, '0')}`
        }
        if (metric.includes('referral')) {
          return `#${Math.floor(Math.random() * (15 - 0 + 1) + 0)
            .toString(16)
            .padStart(2, '0')}0cFF`
        }
        return '#000000'
    }
  }

  const icons = (field: string) => {
    switch (field) {
      case 'stories':
        return iconsEnum.stories
      case 'commentsReels':
      case 'comments':
        return iconsEnum.comments
      case 'likesReels':
      case 'likes':
        return iconsEnum.likes
      case 'participate':
        return iconsEnum.post
      case 'reels':
        return iconsEnum.reels
      case 'video':
        return iconsEnum.video
      case 'tweet':
        return iconsEnum.tweet
      case 'retweet':
        return iconsEnum.retweet
      case 'views':
        return iconsEnum.view
      case 'shares':
        return iconsEnum.share
    }
  }

  const findLargestValueIndices = (arr: Array<number>, x: number) => {
    const sorted = arr.slice().sort((a, b) => b - a)
    const largest = sorted.slice(0, x)
    const indexs = arr?.map((value, index) => (largest.includes(value) ? index : -1)).filter((i) => i !== -1)
    return indexs
  }

  const createData = useCallback(() => {
    if (userRanking) {
      const data = {
        labels: [],
        datasets: [
          {
            label: t('points'),
            data: [],
            backgroundColor: [],
            borderColor: [],
            borderWidth: 1,
          },
        ],
      }

      if (socialNetworkGamification) {
        mainSocialNetworkFields.forEach((field) => {
          if (
            userRanking?.gamification[campaign?.socialNetworks[0]] &&
            userRanking?.gamification[campaign?.socialNetworks[0]][`${field}Total`] > 0
          ) {
            data.labels.push(t(field))
            data.datasets[0].data.push(userRanking?.gamification[campaign?.socialNetworks[0]][`${field}Total`]?.toLocaleString(lang))
            data.datasets[0].backgroundColor.push(parseMetricsColor(field))
            data.datasets[0].borderColor.push(parseMetricsColor(field))
          }
        })
      }

      spreadsheetGamification?.actions?.map((action, i) => {
        if (userRanking?.gamification?.scores && userRanking?.gamification?.scores[action.key] > 0) {
          data.labels.push(action.label)
          data.datasets[0].data.push(userRanking?.gamification?.scores[action.key]?.toLocaleString(lang))
          data.datasets[0].backgroundColor.push(parseMetricsColor(action.key))
          data.datasets[0].borderColor.push(parseMetricsColor(action.key))
        }
      })

      referralGamification?.actions?.map((action, i) => {
        if (userRanking?.gamification?.scores && userRanking?.gamification?.scores[action.key] > 0) {
          data.labels.push(action.label)
          data.datasets[0].data.push(userRanking?.gamification?.scores[action.key]?.toLocaleString(lang))
          data.datasets[0].backgroundColor.push(parseMetricsColor(action.key))
          data.datasets[0].borderColor.push(parseMetricsColor(action.key))
        }
      })

      setRankingToData(data)

      if (
        userRanking.weekRanking?.length &&
        (campaign?.weekRanking || socialNetworkGamification?.config[campaign?.socialNetworks[0]]?.weekRanking)
      ) {
        const totalGroup = campaign?.totalGroup || socialNetworkGamification?.config[campaign?.socialNetworks[0]].totalGroup || 0
        const weekData = {
          labels: [],
          datasets: [
            {
              label: t('points'),
              borderRadius: {
                topRight: 15,
                topLeft: 15,
                bottomRight: 0,
                bottomLeft: 0,
              },
              barThickness: 20,
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        }
        const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary_color')
        userRanking.weekRanking.forEach((week, i) => {
          const startDate = new Date(week.date)
          const endDate = new Date(new Date(week.date).setDate(new Date(week.date).getDate() + 6))
          weekData.labels.push(`${startDate.toLocaleDateString(lang)} - ${endDate.toLocaleDateString(lang)}`)
          weekData.datasets[0].data.push(week.total)
          weekData.datasets[0].backgroundColor.push(primaryColor + '77')
          weekData.datasets[0].borderColor.push(primaryColor)
          // weekData.datasets[0].backgroundColor.push('#E9EBF0')
          // weekData.datasets[0].borderColor.push('#E9EBF0')
        })

        let bestWeeks = []
        if (totalGroup > 0) {
          bestWeeks = findLargestValueIndices(weekData.datasets[0].data, totalGroup)
        }

        if (bestWeeks?.length > 0) {
          bestWeeks.forEach((week) => {
            weekData.datasets[0].backgroundColor[week] = primaryColor
            weekData.datasets[0].borderColor[week] = primaryColor
            // weekData.datasets[0].backgroundColor[week] = '#3B3B3B'
            // weekData.datasets[0].borderColor[week] = '#3B3B3B'
          })
        }

        setWeeklyRanking(weekData)
      }
    }
  }, [userRanking, t, campaign, mainSocialNetworkFields, lang, referralGamification, spreadsheetGamification, socialNetworkGamification])

  useEffect(() => {
    createData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, userRanking])

  useEffect(() => {
    const onLangChangeObservable: SubscriptionLike = onLangChange.subscribe((newLang: 'pt' | 'en' | 'es') => {
      if (newLang !== lang) {
        setLang(newLang)
      }
    })
    return () => {
      onLangChangeObservable?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLangChange, setLang])

  if (!userRanking) {
    return (
      <div className="engage-pointing-page">
        <EmptyPageComponent
          title={t('pointingPage.empty.title')}
          description={t('pointingPage.empty.description')}
          image={rankingEmpty}
          icon={'fa-regular fa-ranking-star'}
        />
      </div>
    )
  }

  return (
    <div className="engage-pointing-page">
      <section>
        <h5>{t('pointingPage.details.title')}</h5>
        <p>
          {t('updatedAt')} {new Date(userRanking.updatedAt).toLocaleDateString(i18n.language)}
        </p>
        <ChartDoughnut
          className="chart"
          style={{
            maxWidth: '230px',
            margin: '0 auto',
          }}
          data={rankingToData}
          center={
            <div className="center-text">
              <h6>{userRanking?.total?.toLocaleString(i18n.language)}</h6>
              <span>{t('pointingPage.details.total')}</span>
            </div>
          }
        />
        <ul className={`legend ${spreadsheetGamification || referralGamification ? 'scores' : ''}`}>
          {socialNetworkGamification &&
            mainSocialNetworkFields?.map((field) => {
              if (typeof userRanking?.gamification[campaign?.socialNetworks[0]][`${field}Total`] !== 'undefined') {
                return (
                  <li key={field}>
                    <i className={`${icons(field)}`} style={{ color: parseMetricsColor(field), lineHeight: 1 }}></i>
                    <span>{t(field)}</span>
                    <span className="text-bold">
                      {userRanking?.gamification[campaign?.socialNetworks[0]][`${field}Total`]?.toLocaleString(lang)} {t('points')}
                    </span>
                  </li>
                )
              }
            })}
          {/* {spreadsheetGamification?.actions?.map((action, i) => {
            if (userRanking?.gamification?.scores && typeof userRanking?.gamification?.scores[action?.key] !== 'undefined') {
              return (
                <li key={`${action?.key}-${i}`}>
                  <i className={`${iconsEnum.scoreRanking}`} style={{ color: parseMetricsColor(action?.key), lineHeight: 1 }}></i>
                  <span>{action?.label}</span>
                  <span className="text-bold">
                    {userRanking?.gamification?.scores[action?.key]?.toLocaleString(lang)} {t('points')}
                  </span>
                </li>
              )
            }
          })} */}
          {referralGamification?.actions?.map((action, i) => {
            if (userRanking?.gamification?.scores && typeof userRanking?.gamification?.scores[action?.key] !== 'undefined') {
              return (
                <li key={`${action?.key}-${i}`}>
                  <i className={`${iconsEnum.referralRanking}`} style={{ color: parseMetricsColor('referral'), lineHeight: 1 }}></i>
                  <span>{action?.label}</span>
                  <span className="text-bold">
                    {userRanking?.gamification?.scores[action?.key]?.toLocaleString(lang)} {t('points')}
                  </span>
                </li>
              )
            }
          })}
        </ul>
      </section>
      {userRanking.weekRanking?.length &&
      (campaign?.weekRanking || socialNetworkGamification?.config[campaign?.socialNetworks[0]]?.weekRanking) ? (
        <section>
          <h5 className="mb-3">{t('pointingPage.week.title')}</h5>
          <ChartHorizontalBarChartjs data={weeklyRanking} />
          <ul className="legend">
            <li>
              <span className="square" style={{ backgroundColor: 'var(--primary_color)' }} />
              <span className="text-bold">{t('pointingPage.week.best')}</span>
            </li>
            <li>
              <span className="square" style={{ backgroundColor: 'var(--primary_color)', opacity: 0.7 }} />
              <span className="text-bold">{t('pointingPage.week.common')}</span>
            </li>
          </ul>
        </section>
      ) : null}
    </div>
  )
}
